import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Loader2, Shield, AlertTriangle, CheckCircle, UserPlus,
} from 'lucide-react';

/**
 * Manage Game Roster dialog (iter279 extraction).
 * Lists eligible players for a game's age category, shows team-membership
 * and prior-RSVP status, and surfaces a cross-team confirmation banner
 * when the picked player belongs to a different team in the same category.
 */
const ManagePlayersDialog = ({
  open,
  onOpenChange,
  selectedGame,
  availablePlayers,
  addPlayerWarning,
  setAddPlayerWarning,
  onAddPlayer,
  submitting,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Game Roster</DialogTitle>
          <DialogDescription>
            {selectedGame && (
              <>vs {selectedGame.opponent_team} • {selectedGame.game_date} at {selectedGame.start_time}</>
            )}
            {selectedGame?.team_name && (
              <Badge className="ml-2 bg-blue-100 text-blue-700">
                <Shield className="h-3 w-3 mr-1" />
                {selectedGame.team_name}
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Cross-team warning confirmation */}
        {addPlayerWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">{addPlayerWarning.warning}</p>
                <p className="text-sm text-amber-700 mt-1">
                  Do you want to add {addPlayerWarning.playerName} anyway?
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddPlayerWarning(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600"
                    onClick={() => onAddPlayer(addPlayerWarning.playerId, true)}
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Anyway'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="py-4">
          {availablePlayers.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No players registered for this age category</p>
          ) : (
            <div className="space-y-2">
              {availablePlayers.map(player => (
                <div
                  key={player.player_id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    player.is_on_game_team
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      player.is_on_game_team ? 'bg-green-200' : 'bg-slate-200'
                    }`}>
                      <span className={`text-sm font-medium ${
                        player.is_on_game_team ? 'text-green-700' : 'text-slate-600'
                      }`}>
                        {player.player_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{player.player_name}</span>
                        {player.is_on_game_team && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Team Member
                          </Badge>
                        )}
                      </div>
                      {player.teams?.length > 0 && !player.is_on_game_team && (
                        <p className="text-xs text-slate-500">
                          On: {player.teams.map(t => t.team_name).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    {player.already_rsvped ? (
                      <Badge className="bg-slate-100 text-slate-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Added
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant={player.is_on_game_team ? "default" : "outline"}
                        onClick={() => onAddPlayer(player.player_id)}
                        disabled={submitting}
                        data-testid={`add-player-${player.player_id}`}
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                          <>
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManagePlayersDialog;
